import { apiRequest } from "./apiClient";
import type {
  AuthorizationAuditLog,
  AuthorizationBusinessUnit,
  AuthorizationConfigResponse,
  AuthorizationExternalGroup,
  AuthorizationRole,
  ExternalGroupCreatePayload,
  ExternalGroupUpdatePayload
} from "../types/authorizationAdmin";

type SuccessResponse<T> = { readonly data: T };

export const getAuthorizationConfig = async (): Promise<AuthorizationConfigResponse> => {
  const response = await apiRequest("/admin/authorization/config");
  const json = (await response.json()) as SuccessResponse<AuthorizationConfigResponse>;
  const data = json.data;
  return {
    ...data,
    bu_scope_module_mappings: data.bu_scope_module_mappings ?? []
  };
};

export type AuditLogQuery = {
  readonly entity_type?: string;
  readonly actor?: string;
  readonly action?: string;
  readonly limit?: number;
  readonly offset?: number;
};

export type AuthorizationAuditLogsPage = {
  readonly data: AuthorizationAuditLog[];
  readonly meta: { readonly total: number; readonly limit: number; readonly offset: number };
};

export const getAuthorizationAuditLogs = async (query: AuditLogQuery = {}): Promise<AuthorizationAuditLogsPage> => {
  const params = new URLSearchParams();
  if (query.entity_type) params.set("entity_type", query.entity_type);
  if (query.actor) params.set("actor", query.actor);
  if (query.action) params.set("action", query.action);
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.offset != null) params.set("offset", String(query.offset));
  const qs = params.toString();
  const response = await apiRequest(`/admin/authorization/audit-logs${qs ? `?${qs}` : ""}`);
  const json = (await response.json()) as {
    readonly success: boolean;
    readonly data: AuthorizationAuditLog[];
    readonly meta: { readonly total: number; readonly limit: number; readonly offset: number };
    readonly message?: string;
  };
  return { data: json.data, meta: json.meta };
};

export const createRole = async (payload: {
  code: string;
  name: string;
  priority: number;
  can_view_all_bu: boolean;
}): Promise<AuthorizationRole> => {
  const response = await apiRequest("/admin/authorization/roles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const json = (await response.json()) as SuccessResponse<AuthorizationRole>;
  return json.data;
};

export const updateRole = async (
  roleId: string,
  payload: {
    name?: string;
    priority?: number;
    can_view_all_bu?: boolean;
    is_active?: boolean;
  }
): Promise<AuthorizationRole> => {
  const response = await apiRequest(`/admin/authorization/roles/${roleId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const json = (await response.json()) as SuccessResponse<AuthorizationRole>;
  return json.data;
};

export const createExternalGroup = async (payload: ExternalGroupCreatePayload): Promise<AuthorizationExternalGroup> => {
  const response = await apiRequest("/admin/authorization/external-groups", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const json = (await response.json()) as SuccessResponse<AuthorizationExternalGroup>;
  return json.data;
};

export const updateExternalGroup = async (
  groupId: string,
  payload: ExternalGroupUpdatePayload
): Promise<AuthorizationExternalGroup> => {
  const response = await apiRequest(`/admin/authorization/external-groups/${groupId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const json = (await response.json()) as SuccessResponse<AuthorizationExternalGroup>;
  return json.data;
};

export const createBusinessUnit = async (payload: {
  code: string;
  name: string;
  description?: string;
  is_active: boolean;
}): Promise<AuthorizationBusinessUnit> => {
  const response = await apiRequest("/admin/authorization/business-units", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const json = (await response.json()) as SuccessResponse<AuthorizationBusinessUnit>;
  return json.data;
};

export const updateBusinessUnit = async (
  businessUnitId: string,
  payload: {
    name?: string;
    /** Send `null` to clear stored description (must be JSON-null, not omitted). */
    description?: string | null;
    is_active?: boolean;
  }
): Promise<AuthorizationBusinessUnit> => {
  const response = await apiRequest(`/admin/authorization/business-units/${businessUnitId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  const json = (await response.json()) as SuccessResponse<AuthorizationBusinessUnit>;
  return json.data;
};
